import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Activity, CreditCard, Droplets, CalendarDays, Weight, Ruler, ArrowRight,
    Building2, Dumbbell, User, TrendingUp, Clock,
} from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/datetime';
import MotivationalBanner from '@/components/MotivationalBanner';

export default function CustomerDashboard() {
    const [data, setData] = useState(null);
    const [phrases, setPhrases] = useState([]);
    const [phrasesRandom, setPhrasesRandom] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await api.get('/customer/dashboard');
                setData(res.data.data);
                // Fetch motivational phrases for customer's gym
                if (res.data.data?.customer?.gym?.id) {
                    try {
                        const phrasesRes = await api.get(`/public/gym/${res.data.data.customer.gym.id}/motivational-phrases`);
                        setPhrases(phrasesRes.data.data || []);
                        setPhrasesRandom(phrasesRes.data.random ?? true);
                    } catch {}
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar el panel.');
            } finally { setLoading(false); }
        };
        fetch();
    }, []);

    if (loading) return <p className="text-white/60 text-center py-8">Cargando...</p>;
    if (error) return <p className="text-white/60 text-center py-8">{error}</p>;
    if (!data) return <p className="text-white/60 text-center py-8">No se encontraron datos.</p>;

    const { customer, active_card, latest_measurement, upcoming_classes, water } = data;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Panel de Alumno</h1>
                <p className="text-sm text-white/60">Hola, {customer.first_name} {customer.last_name}</p>
            </div>

            <MotivationalBanner phrases={phrases} random={phrasesRandom} intervalSeconds={30} />

            {/* Health summary widget */}
            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader className="flex flex-row items-center gap-3 pb-2">
                    <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                        <Activity className="h-4 w-4" />
                    </div>
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide">Datos de salud</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                                <Weight className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-white/60 uppercase tracking-wider">Peso</p>
                                <p className="text-lg font-bold text-white">{latest_measurement?.weight ? `${latest_measurement.weight} kg` : '-'}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                                <Ruler className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-white/60 uppercase tracking-wider">Altura</p>
                                <p className="text-lg font-bold text-white">{latest_measurement?.height ? `${latest_measurement.height} cm` : '-'}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                                <Activity className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-white/60 uppercase tracking-wider">IMC</p>
                                <p className="text-lg font-bold text-white">{latest_measurement?.bmi ?? '-'}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                                <TrendingUp className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-white/60 uppercase tracking-wider">Grasa corporal</p>
                                <p className="text-lg font-bold text-white">{latest_measurement?.body_fat_percentage ? `${latest_measurement.body_fat_percentage}%` : '-'}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                                <Dumbbell className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-white/60 uppercase tracking-wider">Masa muscular</p>
                                <p className="text-lg font-bold text-white">{latest_measurement?.muscle_mass_kg ? `${latest_measurement.muscle_mass_kg} kg` : '-'}</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Middle row: Card status + Water + Upcoming classes */}
            <div className="grid gap-6 lg:grid-cols-3">
                {/* Card status */}
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardHeader className="flex flex-row items-center gap-3 pb-2">
                        <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                            <CreditCard className="h-4 w-4" />
                        </div>
                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">Mi Tarjeta</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {active_card ? (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Badge variant={active_card.status === 'active' ? 'success' : 'secondary'} className="text-xs">
                                        {active_card.status === 'active' ? 'ACTIVA' : 'VENCIDA'}
                                    </Badge>
                                    <span className="text-xs text-white/60">
                                        Vence {formatDate(active_card.end_date)}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className="text-xs text-white/60">Clases usadas</p>
                                        <p className="font-bold text-white">{active_card.used_classes} / {active_card.total_classes}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-white/60">Restantes</p>
                                        <p className="font-bold text-white">{Math.max(0, active_card.total_classes - active_card.used_classes)}</p>
                                    </div>
                                </div>
                                <Link to="/mi-cuenta/tarjeta" className="inline-flex items-center text-xs text-white/60 hover:text-white underline mt-1">
                                    Ver detalles <ArrowRight className="h-3 w-3 ml-1" />
                                </Link>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <CreditCard className="h-8 w-8 text-white/60 mx-auto mb-2" />
                                <p className="text-sm text-white/60">No tenés una tarjeta activa.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Water progress */}
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardHeader className="flex flex-row items-center gap-3 pb-2">
                        <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                            <Droplets className="h-4 w-4" />
                        </div>
                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">Hidratación hoy</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="flex flex-col items-center text-center">
                            <div className="relative h-28 w-28 mb-2">
                                <svg className="h-full w-full -rotate-90" viewBox="0 0 36 36">
                                    <path className="text-[#3a3a3f]" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                                    <path className="text-white transition-all duration-500" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={`${water.percentage}, 100`} strokeLinecap="round" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-xl font-bold text-white">{water.percentage}%</span>
                                </div>
                            </div>
                            <p className="text-sm text-white/60">
                                <span className="font-semibold text-white">{water.consumed_ml} ml</span> de {water.goal_ml} ml
                            </p>
                            <Link to="/mi-cuenta/hidratacion" className="inline-flex items-center text-xs text-white/60 hover:text-white underline mt-2">
                                Ver hidratación <ArrowRight className="h-3 w-3 ml-1" />
                            </Link>
                        </div>
                    </CardContent>
                </Card>

                {/* Gym info */}
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardHeader className="flex flex-row items-center gap-3 pb-2">
                        <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                            <Building2 className="h-4 w-4" />
                        </div>
                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">Mi Gimnasio</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {customer.gym ? (
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    {customer.gym.logo ? (
                                        <img src={`/storage/${customer.gym.logo}`} alt="Logo" className="h-10 w-10 object-contain rounded-lg border border-hairline-dark" />
                                    ) : (
                                        <div className="h-10 w-10 bg-[#0a0a0a] rounded-lg flex items-center justify-center border border-hairline-dark">
                                            <Building2 className="h-5 w-5 text-white/60" />
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-semibold text-white text-sm">{customer.gym.name}</p>
                                        {customer.gym.address && <p className="text-xs text-white/60">{customer.gym.address}</p>}
                                    </div>
                                </div>
                                {customer.gym.phone && (
                                    <p className="text-xs text-white/60">Tel: {customer.gym.phone}</p>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-white/60">Sin gimnasio asignado.</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Upcoming classes */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-white/60 flex items-center gap-2">
                        <CalendarDays className="h-4 w-4" /> Próximas clases
                    </h2>
                    <Link to="/mi-cuenta/clases" className="text-xs text-white/60 hover:text-white underline">
                        Ver historial
                    </Link>
                </div>
                {upcoming_classes.length === 0 ? (
                    <Card className="bg-[#0a0a0a] border-hairline-dark">
                        <CardContent className="py-8 text-center">
                            <CalendarDays className="h-10 w-10 text-white/60 mx-auto mb-3" />
                            <p className="text-white/60 text-sm">No hay clases próximas programadas.</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {upcoming_classes.map(cls => (
                            <Card key={cls.id} className={`bg-[#0a0a0a] border-hairline-dark ${cls.cancelled ? 'opacity-70' : ''}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="font-semibold text-white text-sm">{cls.name}</p>
                                        {cls.cancelled ? (
                                            <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">Cancelada</Badge>
                                        ) : cls.is_registered ? (
                                            <Badge variant="success" className="text-[10px]">Inscripto</Badge>
                                        ) : (
                                            <Badge variant="secondary" className="text-[10px]">Disponible</Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-white/60 mb-1">
                                        <Clock className="h-3 w-3" />
                                        <span>{cls.starts_at}</span>
                                    </div>
                                    {cls.max_capacity !== null && !cls.cancelled && (
                                        <p className="text-xs text-white/60">
                                            Lugares: {cls.spots_left !== null ? `${cls.spots_left} disponibles` : 'Sin límite'}
                                        </p>
                                    )}
                                    {cls.cancelled && cls.cancellation_message && (
                                        <p className="text-xs text-red-300 mt-1">
                                            {cls.cancellation_message}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
